import { useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Facebook, Menu, Phone, Mail, X } from 'lucide-react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { companyInfo, navigation } from '../data/siteData'
import { normalizePath } from '../lib/route'
import { cn } from '../lib/utils'
import { useSiteContent } from '../context/SiteContentContext'

function isActive(currentPath, href) {
  const normalizedCurrent = normalizePath(currentPath)
  const normalizedHref = normalizePath(href)
  if (normalizedHref === '/') return normalizedCurrent === '/'
  return normalizedCurrent === normalizedHref || normalizedCurrent.startsWith(`${normalizedHref}/`)
}

function isMainMenuActive(item, currentPath, classes) {
  if (isActive(currentPath, item.href)) {
    return true
  }

  if (item.children && item.children.length > 0) {
    for (const child of item.children) {
      if (isActive(currentPath, child.href)) {
        return true
      }

      const subItems = child.subItems || (classes ? getClassesForSubmenu(item.label, child.label, classes).map(c => ({
        label: c.name,
        href: `/${c.page_slug}`
      })) : [])

      for (const subItem of subItems) {
        if (isActive(currentPath, subItem.href)) {
          return true
        }
      }
    }
  }

  return false
}

function isSwimTypeOrCat(val) {
  const v = (val || '').toLowerCase()
  return v === 'svømning' || v === 'svoemning' || v === 'svomning'
}

export function getClassCategory(className, classType, classCategory, entry) {
  const name = (className || '').trim()
  const nameLower = name.toLowerCase()
  const typeTerm = entry?.terms?.['class-type']?.[0] || entry?.terms?.['type_tag']?.[0]
  const type = (typeTerm || classType || '').trim().toLowerCase()
  const catTerm = entry?.terms?.['class-category']?.[0] || entry?.terms?.['category_tag']?.[0]
  const cat = (catTerm || classCategory || '').trim().toLowerCase().replace(/_/g, '-')

  const isSwim = isSwimTypeOrCat(type) || isSwimTypeOrCat(cat) || nameLower.includes('svøm') || nameLower.includes('svoem')

  if (isSwim) {
    if (['børnehold', 'boernehold', 'bornehold', 'børn', 'boern', 'born'].includes(cat)) return 'Børnehold'
    if (['voksenhold', 'voksen', 'voksne'].includes(cat)) return 'Voksenhold'
    if (['generelt'].includes(cat)) return 'Generelt'

    const borneholdNames = [
      'baby',
      'babysvømning',
      'leg & plask',
      'begynder 0.-1. kl.',
      'begynder 2.-3. kl.',
      'let øvet',
      'let oeved',
      'familiesvømning',
      'familiesvoemning'
    ]
    const voksenholdNames = [
      'vandskræk',
      'vandskraek',
      'vandgymnastik'
    ]
    if (borneholdNames.some(n => nameLower.includes(n))) return 'Børnehold'
    if (voksenholdNames.some(n => nameLower.includes(n))) return 'Voksenhold'

    const ageLower = (entry?.age || '').toLowerCase()
    if (ageLower.includes('voksen') || ageLower.includes('damer')) return 'Voksenhold'
    if (ageLower.includes('børn') || ageLower.includes('baby') || ageLower.includes('år') || ageLower.includes('kl')) return 'Børnehold'

    return 'Generelt'
  }

  // Explicit category check from CMS if set via CMS taxonomy tag
  if (catTerm) {
    if (['børnehold', 'boernehold', 'bornehold', 'børn', 'boern', 'born'].includes(cat)) return 'Børnehold'
    if (['voksenhold', 'voksen', 'voksne'].includes(cat)) return 'Voksenhold'
    if (['dans/spring', 'dans & spring', 'dans-spring', 'dans', 'spring'].includes(cat)) return 'Dans/Spring'
    if (['senior', 'seniorer'].includes(cat)) return 'Senior'
    if (['special', 'specialhold'].includes(cat)) return 'Special'
    if (['generelt'].includes(cat)) return 'Generelt'
  }

  // Name / historical fallback matching
  // Børnehold
  if (
    (nameLower.includes('forældre/barn') && !nameLower.includes('hit fit')) ||
    nameLower.includes('puslinge') ||
    nameLower.includes('familieholdet') ||
    nameLower.includes('tumleholdet')
  ) {
    return 'Børnehold'
  }

  // Dans/Spring
  if (
    nameLower.includes('hit fit') ||
    nameLower.includes('bhv. + 0. kl.') ||
    nameLower.includes('mgp 1.+2. kl.') ||
    nameLower.includes('teendance') ||
    nameLower.includes('teen dance') ||
    nameLower.includes('minispring') ||
    nameLower.includes('spring 1.-2. kl.') ||
    nameLower.includes('spring drenge') ||
    nameLower.includes('spring piger') ||
    nameLower.includes('spring 6.-8. kl.') ||
    nameLower.includes('sommerspring') ||
    nameLower.includes('showdance')
  ) {
    return 'Dans/Spring'
  }

  // Special
  if (
    nameLower.includes('vidunderbanden') ||
    nameLower.includes('solsikke') ||
    nameLower.includes('solsikkehold') ||
    nameLower.includes('solsikkedance') ||
    nameLower.includes('bevæg dig glad') ||
    nameLower.includes('bevaeg dig glad')
  ) {
    return 'Special'
  }

  // Senior
  if (
    nameLower.includes('idræt om dagen') ||
    nameLower.includes('kol - hjerte - diabetes') ||
    nameLower.includes('kol, diabetes, hjerte') ||
    nameLower.includes('floorball') ||
    nameLower.includes('stolegymnastik') ||
    nameLower.includes('ladies dance mix') ||
    nameLower.includes('ladies dancemix') ||
    nameLower.includes('motion i det fri') ||
    nameLower.includes('krolf') ||
    nameLower.includes('gå-hold') ||
    nameLower.includes('gå hold') ||
    nameLower.includes('gå-holdet')
  ) {
    return 'Senior'
  }

  // Voksenhold
  if (
    nameLower.includes('rytme 15') ||
    nameLower.includes('rytme') ||
    nameLower.includes('rytmedamer') ||
    nameLower.includes('vip') ||
    nameLower.includes('jump') ||
    nameLower.includes('dance fitness') ||
    nameLower.includes('zumba') ||
    (nameLower.includes('yoga') && !nameLower.includes('yin')) ||
    nameLower.includes('yin yoga')
  ) {
    return 'Voksenhold'
  }

  if (cat) {
    if (['børnehold', 'boernehold', 'bornehold', 'børn', 'boern', 'born'].includes(cat)) return 'Børnehold'
    if (['voksenhold', 'voksen', 'voksne'].includes(cat)) return 'Voksenhold'
    if (['dans/spring', 'dans & spring', 'dans-spring', 'dans', 'spring'].includes(cat)) return 'Dans/Spring'
    if (['senior', 'seniorer'].includes(cat)) return 'Senior'
    if (['special', 'specialhold'].includes(cat)) return 'Special'
    if (['generelt'].includes(cat)) return 'Generelt'
  }

  return 'Generelt'
}

function getClassesForSubmenu(itemLabel, childLabel, classes) {
  let filtered = []
  if (itemLabel.startsWith('Gymnastik')) {
    const gymClasses = classes.filter(c => {
      const typeTerm = c.terms?.['class-type']?.[0] || c.terms?.['type_tag']?.[0]
      const catTerm = c.terms?.['class-category']?.[0] || c.terms?.['category_tag']?.[0]
      const cType = typeTerm || c.type || ''
      const cCat = catTerm || c.category || ''
      return !isSwimTypeOrCat(cType) && !isSwimTypeOrCat(cCat) && c.page_slug !== 'showdance'
    })
    filtered = gymClasses.filter(c => getClassCategory(c.name, c.type, c.category, c) === childLabel)
  } else if (itemLabel.startsWith('Svømning')) {
    const swimClasses = classes.filter(c => {
      const typeTerm = c.terms?.['class-type']?.[0] || c.terms?.['type_tag']?.[0]
      const catTerm = c.terms?.['class-category']?.[0] || c.terms?.['category_tag']?.[0]
      const cType = typeTerm || c.type || ''
      const cCat = catTerm || c.category || ''
      return isSwimTypeOrCat(cType) || isSwimTypeOrCat(cCat)
    })
    filtered = swimClasses.filter(c => getClassCategory(c.name, c.type, c.category, c) === childLabel)
  } else {
    return []
  }

  // Sort specifically for Gymnastik 26/27 -> Børnehold
  if ((itemLabel === 'Gymnastik 26/27' || itemLabel === 'Gymnastik 25/26') && childLabel === 'Børnehold') {
    const order = ['forældre/barn', 'puslinge', 'familieholdet', 'tumleholdet']
    return [...filtered].sort((a, b) => {
      const indexA = order.findIndex(term => a.name.toLowerCase().includes(term))
      const indexB = order.findIndex(term => b.name.toLowerCase().includes(term))
      const valA = indexA === -1 ? 999 : indexA
      const valB = indexB === -1 ? 999 : indexB
      return valA - valB
    })
  }

  // Sort specifically for Svømning 26/27 -> Børnehold
  if ((itemLabel === 'Svømning 26/27' || itemLabel === 'Svømning 25/26') && childLabel === 'Børnehold') {
    const order = [
      'baby',
      'leg & plask',
      'begynder 0.-1. kl.',
      'begynder 2.-3. kl.',
      'let øvet',
      'familiesvømning'
    ]
    return [...filtered].sort((a, b) => {
      const indexA = order.findIndex(term => a.name.toLowerCase().includes(term))
      const indexB = order.findIndex(term => b.name.toLowerCase().includes(term))
      const valA = indexA === -1 ? 999 : indexA
      const valB = indexB === -1 ? 999 : indexB
      return valA - valB
    })
  }

  // Sort specifically for Gymnastik 26/27 -> Dans/Spring
  if ((itemLabel === 'Gymnastik 26/27' || itemLabel === 'Gymnastik 25/26') && childLabel === 'Dans/Spring') {
    const order = [
      'hit fit',
      'bhv. + 0. kl.',
      'mgp 1.+2. kl.',
      'teendance',
      'minispring',
      'spring 1.-2. kl.',
      'spring drenge',
      'spring piger',
      'spring 6.-8. kl.'
    ]
    return [...filtered].sort((a, b) => {
      const indexA = order.findIndex(term => a.name.toLowerCase().includes(term))
      const indexB = order.findIndex(term => b.name.toLowerCase().includes(term))
      const valA = indexA === -1 ? 999 : indexA
      const valB = indexB === -1 ? 999 : indexB
      return valA - valB
    })
  }

  // Sort specifically for Gymnastik 26/27 -> Voksenhold
  if ((itemLabel === 'Gymnastik 26/27' || itemLabel === 'Gymnastik 25/26') && childLabel === 'Voksenhold') {
    const order = [
      'rytme 15',
      'rytmedamer',
      'vip',
      'jump',
      'dance fitness',
      'yoga'
    ]
    return [...filtered].sort((a, b) => {
      const indexA = order.findIndex(term => a.name.toLowerCase().includes(term))
      const indexB = order.findIndex(term => b.name.toLowerCase().includes(term))
      const valA = indexA === -1 ? 999 : indexA
      const valB = indexB === -1 ? 999 : indexB
      return valA - valB
    })
  }

  // Sort specifically for Gymnastik 26/27 -> Senior
  if ((itemLabel === 'Gymnastik 26/27' || itemLabel === 'Gymnastik 25/26') && childLabel === 'Senior') {
    const order = [
      'idræt om dagen',
      'kol',
      'floorball',
      'stolegymnastik',
      'ladies dance mix',
      'motion i det fri',
      'krolf',
      'gå-hold'
    ]
    return [...filtered].sort((a, b) => {
      const indexA = order.findIndex(term => a.name.toLowerCase().includes(term))
      const indexB = order.findIndex(term => b.name.toLowerCase().includes(term))
      const valA = indexA === -1 ? 999 : indexA
      const valB = indexB === -1 ? 999 : indexB
      return valA - valB
    })
  }

  return filtered
}

export function getDynamicNavigation(pages = [], classes = []) {
  const pageMap = new Map()
  pages.forEach(p => {
    const slug = p.page_slug || p.slug?._ || p.slug?.da
    if (slug) pageMap.set(slug, p)
  })

  const getPageTitle = (slug, fallback) => {
    const page = pageMap.get(slug)
    return page?.title || fallback
  }

  // 1. Build "Om os" children dynamically
  const defaultOmOsSlugs = [
    { slug: 'vedtaegter', fallback: 'Vedtægter' },
    { slug: 'boerneattester', fallback: 'Børneattester' },
    { slug: 'aarskalender', fallback: 'Årskalender' },
    { slug: 'tilskud', fallback: 'Tilskud' },
    { slug: 'aarets-fighter', fallback: 'Årets fighter' },
    { slug: 'aarets-traener', fallback: 'Årets træner' },
  ]

  const omOsMap = new Map()
  defaultOmOsSlugs.forEach(({ slug, fallback }) => {
    omOsMap.set(slug, {
      label: getPageTitle(slug, fallback),
      href: `/${slug}`,
    })
  })

  pages.forEach(p => {
    const termCat = p.terms?.['page-category']?.[0]
    const cat = (termCat || p.category || '').toLowerCase().replace(/_/g, '-')
    const slug = p.page_slug || p.slug?._ || p.slug?.da
    if (cat === 'om-os' && slug) {
      omOsMap.set(slug, {
        label: p.title || slug,
        href: `/${slug}`,
      })
    }
  })

  // 2. Build "Gymnastik -> Generelt" subItems
  const defaultGymGenerelt = [
    { slug: 'adresse-find-os', fallback: 'Adresse/find os' },
    { slug: 'opvisningstoej', fallback: 'Opvisningstøj' },
    { slug: 'retningslinjer', fallback: 'Retningslinjer' },
    { slug: 'oversigt-hold', fallback: 'Oversigt hold' },
    { slug: 'pigelejr-2026', fallback: 'Pigelejr 2026' },
  ]

  const gymGenereltMap = new Map()
  defaultGymGenerelt.forEach(({ slug, fallback }) => {
    gymGenereltMap.set(slug, {
      label: getPageTitle(slug, fallback),
      href: `/${slug}`,
    })
  })

  pages.forEach(p => {
    const termCat = p.terms?.['page-category']?.[0]
    const cat = (termCat || p.category || '').toLowerCase().replace(/_/g, '-')
    const slug = p.page_slug || p.slug?._ || p.slug?.da
    if ((cat === 'gymnastik-generelt' || cat === 'gymnastik') && slug) {
      gymGenereltMap.set(slug, {
        label: p.title || slug,
        href: `/${slug}`,
      })
    }
  })

  // 3. Build "Svømning -> Generelt" subItems
  const defaultSvomGenerelt = [
    { slug: 'adresse', fallback: 'Adresse' },
    { slug: 'retningslinjer-i-svoemning', fallback: 'Retningslinjer i svømning' },
  ]

  const svomGenereltMap = new Map()
  defaultSvomGenerelt.forEach(({ slug, fallback }) => {
    svomGenereltMap.set(slug, {
      label: getPageTitle(slug, fallback),
      href: `/${slug}`,
    })
  })

  pages.forEach(p => {
    const termCat = p.terms?.['page-category']?.[0]
    const cat = (termCat || p.category || '').toLowerCase().replace(/_/g, '-')
    const slug = p.page_slug || p.slug?._ || p.slug?.da
    if ((cat === 'svomning-generelt' || cat === 'svoemning-generelt') && slug) {
      svomGenereltMap.set(slug, {
        label: p.title || slug,
        href: `/${slug}`,
      })
    }
  })

  // 4. Construct main navigation structure
  const navTree = [
    {
      label: getPageTitle('gymnastik-26-27', 'Gymnastik 26/27'),
      href: '/gymnastik-26-27',
      children: [
        { label: 'Børnehold', href: '/gymnastik-26-27/bornehold' },
        { label: 'Dans/Spring', href: '/gymnastik-26-27/dans-spring' },
        { label: 'Voksenhold', href: '/gymnastik-26-27/voksenhold' },
        { label: 'Senior', href: '/gymnastik-26-27/senior' },
        { label: 'Special', href: '/gymnastik-26-27/special' },
        {
          label: 'Generelt',
          href: '/gymnastik-26-27',
          subItems: Array.from(gymGenereltMap.values()),
        },
      ],
    },
    {
      label: getPageTitle('svomning-26-27', 'Svømning 26/27'),
      href: '/svomning-26-27',
      children: [
        { label: 'Børnehold', href: '/svomning-26-27/bornehold' },
        { label: 'Voksenhold', href: '/svomning-26-27/voksenhold' },
        {
          label: 'Generelt',
          href: '/svomning-26-27',
          subItems: Array.from(svomGenereltMap.values()),
        },
      ],
    },
    {
      label: getPageTitle('om-os', 'Om os'),
      href: '/om-os',
      children: Array.from(omOsMap.values()),
    },
    { label: getPageTitle('bestyrelse', 'Bestyrelse'), href: '/bestyrelse', children: [] },
    { label: getPageTitle('galleri', 'Galleri'), href: '/galleri', children: [] },
    { label: getPageTitle('kontakt', 'Kontakt'), href: '/kontakt', children: [] },
    { label: getPageTitle('tilmelding', 'Tilmelding'), href: '/tilmelding', children: [] },
  ]

  // Add extra top-level pages created in CMS with category === 'hovedmenu' / 'topmenu' / 'menu'
  pages.forEach(p => {
    const termCat = p.terms?.['page-category']?.[0]
    const cat = (termCat || p.category || '').toLowerCase()
    const slug = p.page_slug || p.slug?._ || p.slug?.da
    if ((cat === 'hovedmenu' || cat === 'topmenu' || cat === 'menu') && slug) {
      if (!navTree.some(item => item.href === `/${slug}`)) {
        navTree.push({
          label: p.title || slug,
          href: `/${slug}`,
          children: [],
        })
      }
    }
  })

  return navTree
}

function MobileNavItem({ item, depth = 0, parentLabel = '', onNavigate, classes }) {
  const [open, setOpen] = useState(false)
  const location = useLocation()

  const children = item.children || []
  
  const subItems = useMemo(() => {
    if (depth === 1) {
      return item.subItems || getClassesForSubmenu(parentLabel, item.label, classes).map(c => ({
        label: c.name,
        href: `/${c.page_slug}`
      }))
    }
    return []
  }, [depth, parentLabel, item, classes])

  const hasChildren = children.length > 0
  const hasSubItems = subItems.length > 0
  const isExpandable = hasChildren || hasSubItems

  const isUnclickable = useMemo(() => {
    if (depth === 1) {
      return (parentLabel.startsWith('Gymnastik') && ['Børnehold', 'Dans/Spring', 'Voksenhold', 'Senior', 'Special', 'Generelt'].includes(item.label)) ||
             (parentLabel.startsWith('Svømning') && ['Børnehold', 'Voksenhold', 'Generelt'].includes(item.label));
    }
    return false
  }, [depth, parentLabel, item.label])

  const isChildActive = useMemo(() => {
    if (isActive(location.pathname, item.href)) return true
    if (subItems && subItems.length > 0) {
      return subItems.some(subItem => isActive(location.pathname, subItem.href))
    }
    return false
  }, [item, subItems, location.pathname])

  const mainActive = depth === 0 ? isMainMenuActive(item, location.pathname, classes) : false

  return (
    <div className={cn(
      "py-1.5",
      depth === 0 ? "border-b border-white/10 py-3 last:border-b-0" : "pl-3"
    )}>
      <div className="flex items-center justify-between gap-2">
        {isUnclickable ? (
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className={cn(
              "text-left text-sm font-medium transition w-full py-1",
              (open || isChildActive) ? "text-[#3B70E5]" : "text-slate-300 hover:text-white"
            )}
          >
            {item.label}
          </button>
        ) : (
          <NavLink
            to={item.href}
            onClick={onNavigate}
            className={({ isActive: active }) =>
              cn(
                "text-sm transition hover:text-[#3B70E5] py-1",
                depth === 0 ? "font-semibold text-slate-100" : "text-slate-300",
                (active || mainActive || isChildActive) && "text-[#3B70E5] font-semibold"
              )
            }
          >
            {item.label}
          </NavLink>
        )}

        {isExpandable && (
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="rounded-lg border border-white/10 p-1.5 text-slate-300 transition hover:bg-white/5 hover:text-white"
            aria-label={open ? `Skjul ${item.label}` : `Vis ${item.label}`}
          >
            <ChevronDown className={cn("h-4 w-4 transition duration-200", open && "rotate-180")} />
          </button>
        )}
      </div>

      {open && (
        <div className="mt-2 grid gap-1.5 border-l border-white/10 pl-2">
          {hasChildren && children.map((child) => (
            <MobileNavItem
              key={child.label}
              item={child}
              depth={depth + 1}
              parentLabel={item.label}
              onNavigate={onNavigate}
              classes={classes}
            />
          ))}
          {hasSubItems && subItems.map((subItem) => (
            <NavLink
              key={subItem.href}
              to={subItem.href}
              onClick={onNavigate}
              className={({ isActive: active }) =>
                cn(
                  "block rounded-lg px-3 py-1.5 text-xs text-slate-400 transition hover:bg-white/5 hover:text-white",
                  active && "bg-[#3B70E5]/10 text-[#3B70E5] font-medium"
                )
              }
            >
              {subItem.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  )
}

export function SiteChrome({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const location = useLocation()
  const { classes, pages } = useSiteContent()

  const navigationTree = useMemo(() => {
    return getDynamicNavigation(pages, classes)
  }, [pages, classes])

  const activeItem = useMemo(() => {
    return navigationTree.find((item) => isMainMenuActive(item, location.pathname, classes)) || null
  }, [location.pathname, classes, navigationTree])

  return (
    <div className="min-h-screen bg-stone-50 text-slate-900 antialiased">
      <header className="sticky top-0 z-50">
        <div className="border-b border-slate-200/80 bg-stone-100/95 backdrop-blur">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8">
            <Link to="/" className="group flex items-center">
              <img
                src="https://assets.auxodev.dk/assets/b6209d66-5898-40d7-bfd5-6255faf6b7d0/06561010-dd14-4ee4-b6ee-d0e005e5d5db.png"
                alt="Varde Gymnastikforening Logo"
                className="h-12 lg:h-[95px] w-auto object-contain transition group-hover:scale-105"
              />
            </Link>

            <div className="hidden items-center gap-3 min-[1200px]:flex">
              <a
                href={`tel:${companyInfo.phone.replace(/\s+/g, '')}`}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-[#3B70E5] hover:text-white hover:border-[#3B70E5]"
                aria-label="Ring til os"
              >
                <Phone className="h-4 w-4" />
              </a>
              <a
                href={`mailto:${companyInfo.email}`}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-[#3B70E5] hover:text-white hover:border-[#3B70E5]"
                aria-label="Send e-mail"
              >
                <Mail className="h-4 w-4" />
              </a>
              <a
                href={companyInfo.facebook}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-[#3B70E5] hover:text-white hover:border-[#3B70E5]"
                aria-label="Facebook"
              >
                <Facebook className="h-4 w-4" />
              </a>
            </div>

            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 shadow-sm transition hover:border-slate-400 min-[1200px]:hidden"
              onClick={() => setMobileOpen((value) => !value)}
              aria-expanded={mobileOpen}
              aria-label={mobileOpen ? 'Luk menu' : 'Åbn menu'}
            >
              {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
              Menu
            </button>
          </div>
        </div>

        <nav className="border-b border-slate-800/70 bg-slate-950 text-white shadow-sm">
          <div className="mx-auto hidden max-w-7xl items-center justify-center px-4 py-4 min-[1200px]:flex sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-center justify-center gap-1.5">
              {navigationTree.map((item) => {
                const active = isMainMenuActive(item, location.pathname, classes)
                return item.children.length ? (
                  <div key={item.label} className="group relative">
                    <NavLink
                      to={item.href}
                      className={cn(
                        'inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium transition',
                        active
                          ? 'bg-[#3B70E5] text-white'
                          : 'text-slate-100 hover:bg-white/10 hover:text-white',
                      )}
                    >
                      {item.label}
                      <ChevronDown className="h-4 w-4" />
                    </NavLink>
                    <div className="invisible absolute left-0 top-full z-50 mt-2 min-w-64 translate-y-1 rounded-2xl border border-slate-800 bg-white p-2 opacity-0 shadow-2xl transition duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 before:absolute before:inset-x-0 before:-top-3 before:h-3">
                      {item.children.map((child) => {
                        const subItems = child.subItems || getClassesForSubmenu(item.label, child.label, classes).map(c => ({
                          label: c.name,
                          href: `/${c.page_slug}`
                        }))
                        const hasSubItems = subItems.length > 0
                        const isUnclickable = (item.label.startsWith('Gymnastik') && ['Børnehold', 'Dans/Spring', 'Voksenhold', 'Senior', 'Special', 'Generelt'].includes(child.label)) ||
                                              (item.label.startsWith('Svømning') && ['Børnehold', 'Voksenhold', 'Generelt'].includes(child.label));

                        const isChildActive = isActive(location.pathname, child.href) || (subItems && subItems.some(subItem => isActive(location.pathname, subItem.href)))

                        const content = (
                          <>
                            <span>{child.label}</span>
                            {hasSubItems && (
                              <ChevronRight className="h-3.5 w-3.5 text-slate-400 transition group-hover/sub:translate-x-0.5" />
                            )}
                          </>
                        )

                        const className = cn(
                          'flex items-center justify-between rounded-xl px-3 py-2 text-sm transition hover:bg-[#3B70E5]/10 hover:text-[#3B70E5] w-full text-left',
                          isUnclickable ? 'cursor-default text-slate-700 font-medium' : 'text-slate-700'
                        )

                        return (
                          <div key={child.label} className="group/sub relative">
                            {isUnclickable ? (
                              <div className={cn(className, isChildActive ? 'bg-[#3B70E5]/10 font-semibold text-[#3B70E5]' : '')}>
                                {content}
                              </div>
                            ) : (
                              <NavLink
                                to={child.href}
                                className={({ isActive: childActive }) =>
                                  cn(
                                    className,
                                    (childActive || isChildActive) ? 'bg-[#3B70E5]/10 font-semibold text-[#3B70E5]' : '',
                                  )
                                }
                              >
                                {content}
                              </NavLink>
                            )}

                            {hasSubItems && (
                              <div className="invisible absolute left-full top-0 z-50 ml-1 min-w-64 -translate-x-1 rounded-2xl border border-slate-200 bg-white p-2 opacity-0 shadow-2xl transition duration-200 group-hover/sub:visible group-hover/sub:translate-x-0 group-hover/sub:opacity-100 before:absolute before:inset-y-0 before:-left-3 before:w-3">
                                {subItems.map((subItem) => (
                                  <NavLink
                                    key={subItem.href}
                                    to={subItem.href}
                                    className={({ isActive: subActive }) =>
                                      cn(
                                        'block rounded-xl px-3 py-2 text-sm transition hover:bg-[#3B70E5]/10 hover:text-[#3B70E5]',
                                        subActive ? 'bg-[#3B70E5]/10 font-semibold text-[#3B70E5]' : 'text-slate-700',
                                      )
                                    }
                                  >
                                    {subItem.label}
                                  </NavLink>
                                ))}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ) : (
                  <NavLink
                    key={item.label}
                    to={item.href}
                    className={({ isActive: itemActive }) =>
                      cn(
                        'rounded-full px-4 py-2 text-sm font-medium transition',
                        itemActive || active
                          ? 'bg-[#3B70E5] text-white'
                          : 'text-slate-100 hover:bg-white/10 hover:text-white',
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                )
              })}
            </div>
          </div>
        </nav>
      </header>

      {/* Mobile Sidebar Drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 min-[1200px]:hidden">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm transition-opacity"
            onClick={() => setMobileOpen(false)}
          />
          
          {/* Drawer Panel */}
          <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-xs flex-col bg-slate-950 text-white shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
              <img
                src="https://assets.auxodev.dk/assets/b6209d66-5898-40d7-bfd5-6255faf6b7d0/06561010-dd14-4ee4-b6ee-d0e005e5d5db.png"
                alt="Varde Gymnastikforening Logo"
                className="h-10 w-auto object-contain"
              />
              <button
                type="button"
                className="rounded-full border border-white/10 p-2 text-slate-300 transition hover:bg-white/5 hover:text-white"
                onClick={() => setMobileOpen(false)}
                aria-label="Luk menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Navigation Content (Scrollable) */}
            <div className="flex-1 overflow-y-auto px-6 py-6">
              <div className="grid gap-1">
                {navigationTree.map((item) => (
                  <MobileNavItem
                    key={item.label}
                    item={item}
                    depth={0}
                    onNavigate={() => setMobileOpen(false)}
                    classes={classes}
                  />
                ))}
              </div>
            </div>

            {/* Footer / Contact info */}
            <div className="border-t border-white/10 bg-slate-900/50 px-6 py-6">
              <div className="grid gap-3 text-sm text-slate-300">
                <a 
                  href={`tel:${companyInfo.phone.replace(/\s+/g, '')}`}
                  className="inline-flex items-center gap-2 transition hover:text-white"
                >
                  <Phone className="h-4 w-4 text-[#3B70E5]" />
                  <span className="whitespace-nowrap">{companyInfo.phone}</span>
                </a>
                <a 
                  href={`mailto:${companyInfo.email}`}
                  className="inline-flex items-center gap-2 transition hover:text-white"
                >
                  <Mail className="h-4 w-4 text-[#3B70E5]" />
                  <span className="whitespace-nowrap">{companyInfo.email}</span>
                </a>
                <a 
                  href={companyInfo.facebook}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 transition hover:text-white"
                >
                  <Facebook className="h-4 w-4 text-[#3B70E5]" />
                  <span>Facebook</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeItem ? (
        <div className="border-b border-slate-200 bg-white/80">
          <div className="mx-auto max-w-7xl px-4 py-2 text-xs text-slate-500 sm:px-6 lg:px-8">
            {activeItem.label}
          </div>
        </div>
      ) : null}

      <main id="main-content">{children}</main>

      <footer className="bg-slate-950 text-slate-100">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-4 lg:px-8">
          <div>
            <img
              src="https://assets.auxodev.dk/assets/b6209d66-5898-40d7-bfd5-6255faf6b7d0/06561010-dd14-4ee4-b6ee-d0e005e5d5db.png"
              alt="Varde Gymnastikforening Logo"
              className="h-[65px] w-auto object-contain"
            />
            <p className="mt-4 max-w-sm text-sm leading-6 text-slate-300">
              Gymnastikforeningen er stiftet 19. maj 2005 efter en spaltning af Varde Idrætsforening. Foreningen dækker i dag et bredt udvalg af gymnastik og svømning fra de yngste til de ældste.
            </p>
            <p className="mt-4 text-sm leading-7 text-slate-300">Sæsonstart: {companyInfo.seasonStart}</p>
            <a
              href={companyInfo.facebook}
              target="_blank"
              rel="noreferrer"
              className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm font-medium text-white transition hover:bg-[#3B70E5] hover:text-white"
            >
              <Facebook className="h-4 w-4" />
              Facebook
            </a>
          </div>

          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#3B70E5]">Kontakt</p>
            <div className="mt-4 grid gap-3 text-sm text-slate-300">
              <a className="whitespace-nowrap transition hover:text-white" href={`tel:${companyInfo.phone.replace(/\s+/g, '')}`}>
                {companyInfo.phone}
              </a>
              <a className="whitespace-nowrap transition hover:text-white" href={`mailto:${companyInfo.email}`}>
                {companyInfo.email}
              </a>
              <p className="text-slate-400">Chr Richardts Vej 5, 6800 Varde</p>
            </div>
          </div>

          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#3B70E5]">Hurtige links</p>
            <div className="mt-4 grid gap-2 text-sm text-slate-300">
              {navigationTree.map((item) => (
                <Link key={item.label} to={item.href} className="transition hover:text-white">
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#3B70E5]">Aktuelt</p>
            <p className="mt-4 text-sm leading-7 text-slate-300">
              Hold, fællesskab, bevægelse og praktisk info samlet ét sted.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/tilmelding" className="rounded-full bg-[#3B70E5] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#3B70E5]/90">
                Tilmelding
              </Link>
              <Link to="/kontakt" className="rounded-full border border-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/5">
                Kontakt
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
